import { apiClient } from "../../lib/http/apiClient";
import { adminStorage } from "../admin/service";
import type { ApprovalRequired, Paged } from "./types";

const BASE = "/api/admin/gamification/referral";

function adminHeaders() {
  const token = adminStorage.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const REFERRAL_PAIRS = ["seller_seller", "seller_buyer", "buyer_seller", "buyer_buyer"] as const;
export type ReferralPair = (typeof REFERRAL_PAIRS)[number];
export interface ReferralAmounts { referrer: number; referred: number }

export interface ReferralSettings {
  referral_enabled: boolean;
  monthly_limit: number;
  cluster_flag_threshold: number;
  block_same_network_and_device: boolean;
  whatsapp_enabled?: boolean;
  amounts: Record<ReferralPair, ReferralAmounts>;
  gamification_enabled?: boolean;
  updated_at?: string | null;
  updated_by?: string | null;
}

export type ReferralStatus = "attached" | "flagged" | "blocked" | "rewarding" | "rewarded" | "skipped" | "rejected" | "reversed";

export interface ReferralRow {
  id: string;
  status: ReferralStatus;
  reason: string | null;
  fraud_flags: string[];
  code: string | null;
  referrer_id: string;
  referrer_name: string | null;
  referrer_email: string | null;
  referrer_phone: string | null;
  referrer_role: string | null;
  referred_id: string;
  referred_name: string | null;
  referred_email: string | null;
  referred_phone: string | null;
  referred_role: string | null;
  referrer_credits: number;
  referred_credits: number;
  created_at: string | null;
  rewarded_at: string | null;
  decided_by: string | null;
  decision_note: string | null;
  status_note?: string | null;
}

export interface ReferralList extends Paged<ReferralRow> {
  status_counts: Record<string, number>;
}

export type ReferralAction = "flag" | "approve" | "reject" | "reverse";

export const referralService = {
  getSettings: async (): Promise<ReferralSettings> => {
    const res = await apiClient.get(`${BASE}/settings`, { headers: adminHeaders() });
    return res.data;
  },

  /** With two-admin approval on, raising amounts / the limit or switching on is only requested (HTTP 202). */
  updateSettings: async (body: Partial<Omit<ReferralSettings, "amounts">> & { amounts?: Partial<Record<ReferralPair, ReferralAmounts>> }): Promise<ReferralSettings | ApprovalRequired> => {
    const res = await apiClient.put(`${BASE}/settings`, body, { headers: adminHeaders() });
    return res.data;
  },

  list: async (page: number, status: string, q: string): Promise<ReferralList> => {
    const res = await apiClient.get(`${BASE}/list`, {
      params: { page, page_size: 25, ...(status ? { status } : {}), ...(q.trim() ? { q: q.trim() } : {}) },
      headers: adminHeaders(),
    });
    return res.data;
  },

  act: async (id: string, action: ReferralAction, reason: string): Promise<Record<string, unknown>> => {
    const res = await apiClient.post(`${BASE}/${encodeURIComponent(id)}/${action}`, { reason }, { headers: adminHeaders() });
    return res.data;
  },

  exportCsv: async (status: string, q: string): Promise<void> => {
    const res = await apiClient.get(`${BASE}/export.csv`, {
      params: { ...(status ? { status } : {}), ...(q.trim() ? { q: q.trim() } : {}) },
      headers: adminHeaders(),
      responseType: "blob",
    });
    const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "gamification-referrals.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};
